import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Link, Paragraph, Popup, showToast } from '@quickmd-llc/design-system'
import { usePostPatientsPatientIdUnmerge } from '@/api/generated/patient/patient'
import { ADMIN_EVENT_NAMES, tracking } from '@/lib/segment/tracking'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { selectPatientInfo } from '@/store/charts/chartsSelectors'
import { type Patient } from '@/types/patient'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { getPatientInfo } from '@/store/charts/ChartsThunk'

export function DuplicateAccountsSection() {
  const dispatch = useAppDispatch()
  const [params] = useSearchParams()
  const patientInfo = useAppSelector(selectPatientInfo)

  const [popupVisible, setPopupVisible] = useState<boolean>(false)

  const id = params.get('id') || ''
  const patient: Patient | undefined = patientInfo[id]

  const { mutate, isPending } = usePostPatientsPatientIdUnmerge({
    mutation: {
      onSuccess: () => {
        tracking.trackEvent(ADMIN_EVENT_NAMES.MERGED_PATIENT_CHART_UNMERGE_ACCOUNT_UNMERGE)
        // @ts-expect-error TS2351 TEMP ERROR SUPPRESSION: CRA -> VITE
        dispatch(getPatientInfo(id))
        setPopupVisible(false)
      },
      onError: (error) => {
        console.error('Error unmerging patient account:', error)
        showToast({
          type: 'error',
          message: 'An error occurred while trying to unmerge the patient account. Please try again later.',
          position: 'bottom-right',
        })
      },
    },
  })

  if (!patient) {
    return null
  }

  const { IsMergedMainAccount, MergeMetadata, RestrictionStatus } = patient

  if (!IsMergedMainAccount && RestrictionStatus !== 'merged') {
    return null
  }

  function handleClickMrn() {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGED_PATIENT_CHART_CLICK_MRN_LINK)
  }

  function handleClickUnmerge() {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGED_PATIENT_CHART_UNMERGE_ACCOUNT)

    setPopupVisible(true)
  }

  function handleClickConfirmUnmerge() {
    mutate({ patientId: id })
  }

  function handleCancel() {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGED_PATIENT_CHART_UNMERGE_ACCOUNT_CANCEL)

    setPopupVisible(false)
  }

  return (
    <>
      {popupVisible ?
        <Popup
          isOpen
          header='Are you sure you want to unmerge this account?'
          body={
            <Paragraph>
              {IsMergedMainAccount ?
                'This action will unmerge all linked accounts from this patient chart. Verify the account details before proceeding.'
              : 'Unmerging will separate this account from the merged patient chart. Make sure you’ve reviewed the account details before confirming.'
              }
            </Paragraph>
          }
          footer={
            <div className='flex justify-end gap-sp-1'>
              <Button onClick={() => handleCancel()} priority='secondary' disabled={isPending}>
                Cancel
              </Button>

              <Button onClick={() => handleClickConfirmUnmerge()} loading={isPending}>
                Unmerge
              </Button>
            </div>
          }
          lockOpen={isPending}
          showCloseIcon
          onClose={() => handleCancel()}
        />
      : null}

      <div className='mb-sp-2 flex items-center justify-between rounded border border-border-medium bg-white p-sp-2'>
        {IsMergedMainAccount ?
          <div className='grid gap-sp-1'>
            <Paragraph className='font-medium text-text-dark'>Main account</Paragraph>

            <Paragraph size='sm'>
              The following MRNs were merged into this account:{' '}
              {MergeMetadata.SourceAccountsMetadata.map((account, index) => (
                <>
                  <Link key={account.Id} to={`/patient-chart?id=${account.Id}`} onClick={() => handleClickMrn()}>
                    MRN {account.MedicalRecordNumber}
                  </Link>

                  {index < MergeMetadata.SourceAccountsMetadata.length - 1 && ', '}
                </>
              ))}
              . To undo this action select Unmerge account.
            </Paragraph>
          </div>
        : <div className='grid gap-sp-1'>
            <Paragraph className='font-medium text-text-dark'>
              This account has been merged into{' '}
              <Link to={`/patient-chart?id=${MergeMetadata?.MainAccountId}`} onClick={() => handleClickMrn()}>
                MRN {MergeMetadata?.MainAccountMedicalRecordNumber}
              </Link>
              .
            </Paragraph>

            <Paragraph size='sm'>
              If this action was made in error, you can undo it by selecting Unmerge account.
            </Paragraph>
          </div>
        }

        <Button onClick={() => handleClickUnmerge()} priority='secondary' className='flex-shrink-0'>
          Unmerge account
        </Button>
      </div>
    </>
  )
}
